import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Simple month calendar with blocked dates
export default function AvailabilityCalendar({ blockedDates = [], checkIn, checkOut, onSelect }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [viewMonth, setViewMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const blocked = useMemo(() => new Set(blockedDates), [blockedDates]);

  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const days = useMemo(() => {
    const first = new Date(viewMonth);
    const lastDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const startWeekday = (first.getDay() + 6) % 7; // Mon=0
    const out = [];
    for (let i = 0; i < startWeekday; i++) out.push(null);
    for (let d = 1; d <= lastDay; d++) out.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
    return out;
  }, [viewMonth]);

  const monthLabel = viewMonth.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const ci = checkIn ? new Date(checkIn) : null;
  const co = checkOut ? new Date(checkOut) : null;

  const isInRange = (d) => ci && co && d >= ci && d <= co;
  const isPast = (d) => d < today;

  const handleClick = (d) => {
    if (!d || isPast(d) || blocked.has(fmt(d))) return;
    if (!ci || (ci && co)) {
      onSelect({ check_in: fmt(d), check_out: "" });
      return;
    }
    if (d <= ci) {
      onSelect({ check_in: fmt(d), check_out: "" });
      return;
    }
    // ensure no blocked date between
    const range = [];
    const cur = new Date(ci);
    while (cur < d) { range.push(fmt(cur)); cur.setDate(cur.getDate() + 1); }
    if (range.some(r => blocked.has(r))) {
      onSelect({ check_in: fmt(d), check_out: "" });
      return;
    }
    onSelect({ check_in: fmt(ci), check_out: fmt(d) });
  };

  const prev = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  const next = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  const canPrev = viewMonth > new Date(today.getFullYear(), today.getMonth(), 1);

  return (
    <div data-testid="availability-calendar" className="select-none">
      <div className="flex items-center justify-between mb-5">
        <button data-testid="cal-prev" onClick={prev} disabled={!canPrev} className="w-9 h-9 border border-white/15 hover:border-gold disabled:opacity-30 flex items-center justify-center text-white/80">
          <ChevronLeft size={16} />
        </button>
        <h4 className="font-serif text-lg text-white">{monthLabel}</h4>
        <button data-testid="cal-next" onClick={next} className="w-9 h-9 border border-white/15 hover:border-gold flex items-center justify-center text-white/80">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2 text-[0.65rem] uppercase tracking-[0.18em] text-white/40 text-center">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const k = fmt(d);
          const isBlocked = blocked.has(k);
          const past = isPast(d);
          const inRange = isInRange(d);
          const isStart = ci && fmt(ci) === k;
          const isEnd = co && fmt(co) === k;
          return (
            <button
              key={i}
              data-testid={`cal-day-${k}`}
              onClick={() => handleClick(d)}
              disabled={past || isBlocked}
              className={`aspect-square text-sm flex items-center justify-center transition-colors
                ${past ? "text-white/15 cursor-not-allowed" : ""}
                ${isBlocked ? "text-white/25 line-through cursor-not-allowed bg-[#1A221E]" : ""}
                ${!past && !isBlocked && !inRange ? "text-white/80 hover:bg-gold/15 hover:text-gold border border-transparent hover:border-gold/40" : ""}
                ${inRange && !isStart && !isEnd ? "bg-emerald/30 text-white" : ""}
                ${isStart || isEnd ? "bg-gold text-black font-semibold" : ""}
              `}
            >{d.getDate()}</button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 mt-5 text-[0.7rem] text-white/50">
        <span className="flex items-center gap-2"><span className="w-3 h-3 bg-gold inline-block" />Selected</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 bg-emerald/40 inline-block" />In range</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 bg-[#1A221E] inline-block border border-white/10" />Booked</span>
      </div>
    </div>
  );
}
